import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {Router} from '@angular/router';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from "@ngneat/transloco";
import {ORGANISER_EDIT} from 'src/app/app-routing.module';
import {AbstractComponent} from "@components";
import {
  ApplicationProcedureTypes,
  Category, DistanceMarkers,
  EventItem,
  GenderSeparationTypes,
  Lookups,
  PenaltyMinutesTypes,
  Race,
  RaceSettings, RegistrationDisplayFlags,
  TeamRankingTeamSeparationTypes,
  TimingSystem
} from "@model";
import {LoaderService, RaceSettingsService, TOAST_STATE, ToastService} from "@services";
import {required} from "@utility";
import {TeamType} from 'src/app/model/team-type';
import {take} from "rxjs";

@Component({
  selector: 'app-race-details-settings',
  templateUrl: './race-details-settings.component.html',
  styleUrls: ['./race-details-settings.component.scss']
})
export class RaceDetailsSettingsComponent extends AbstractComponent implements OnInit {
  @Input() raceSettings: RaceSettings | undefined = new RaceSettings();
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() disableEditing: boolean = false;
  @Input() categoryList: Category[] = [];
  @Input() applicationProcedureTypes: Lookups[] = [];
  @Input() typeOfApplicationTypes: Lookups[] = [];
  @Input() teamTypes: Lookups[] = [];
  @Input() selectionOfTeamMembersTypes: Lookups[] = [];
  @Input() measurementTypes: Lookups[] = [];
  @Input() measurementPrecisionTypes: Lookups[] = [];
  @Input() startByTypeTypes: Lookups[] = [];
  @Input() genderSeparationTypes: Lookups[] = [];
  @Input() teamRankingTeamSeparationTypes: Lookups[] = [];
  @Input() rotatingOrderTypes: any[] = [];
  @Input() timingSystems: Lookups[] = [];
  @Input() timingEquipments: Lookups[] = [];
  @Input() penaltyMinutesTypes: Lookups[] = [];
  @Output() raceSettingsChange: EventEmitter<RaceSettings | undefined> = new EventEmitter<RaceSettings | undefined>();

  @ViewChild('categorySelect') dropdown?: NgbDropdown;

  inputForm = new FormGroup({
    applicationProcedure: new FormControl(),
    typeOfApplication: new FormControl(),
    teamType: new FormControl(),
    selectionOfTeamMembersType: new FormControl(),
    teamNameRequired: new FormControl(),
    genderRequired: new FormControl(),
    measurementType: new FormControl(),
    measurementPrecisionType: new FormControl(),
    startByType: new FormControl(),
    resultsPerPage: new FormControl(),
    topRanked: new FormControl(),
    teamParticipantsForRanking: new FormControl(),
    teamRankingGenderSeparation: new FormControl(),
    rankingGroups: new FormControl(),
    participantsFromRankingGroup: new FormControl(),
    groupRankingSeparation: new FormControl(),
    teamRankingTeamSeparation: new FormControl(),
    penaltyMinutes: new FormControl(),
    controlPoints: new FormControl(),
    minimumLapDuration: new FormControl(),
    rotatingOrder: new FormControl(),
    rotatingResultsDisplay: new FormControl(),
    rotatingResultsSeconds: new FormControl(),
    pic2GoEventId: new FormControl(),
    pic2GoEventUniqueCode: new FormControl(),
    registrationExternalUrl: new FormControl(),
    category: new FormControl(),
    maxGroupRegisteredParticipants: new FormControl(),
    useGroupRanking: new FormControl(),
    timingSystem: new FormControl(),
    timingEquipment: new FormControl(),
    enableGroupRegistrationPayment: new FormControl(),
    usingUploadRoute: new FormControl(),
    displayControlPoints: new FormControl(),
    showRouteProfile: new FormControl(),
    enableGPXDownload: new FormControl(),
    usingDistanceMarkersOnRoute: new FormControl(),
    distanceMarkersOnRoute: new FormControl(),
    maxParticipantsInStartBlock: new FormControl(),
    blockStart: new FormControl()
  });

  distanceMarkers: Map<number, string> = new Map<number, string>();

  constructor(public translocoService: TranslocoService,
              private raceSettingsService: RaceSettingsService, private toastService: ToastService,
              private router: Router, private loaderService: LoaderService
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToValueChanges();
    this.initForm();
    this.raceSettingsChanged();
    this.distanceMarkers = this.getDistanceMarkers();
  }

  private raceSettingsChanged() {
    if (this.raceSettings) {
      this.initDefaultFields();
      this.inputForm.patchValue(this.raceSettings);
      if (this.raceSettings.rankingGroups && this.raceSettings.useGroupRanking) {
        let rankingGroups: string = this.raceSettings.rankingGroups;
        if (rankingGroups.endsWith(";")) {
          rankingGroups = rankingGroups.slice(0, -1);
        }
        this.inputForm.controls.rankingGroups.setValue(rankingGroups.split(";"))
      }
      this.inputForm.controls.category.setValue(this.raceSettings.category?.id);
    }
    if (!this.inputForm.controls.enableGroupRegistrationPayment.value) {
      this.inputForm.controls.enableGroupRegistrationPayment.setValue(false);
      this.inputForm.controls['maxGroupRegisteredParticipants'].disable();
    } else {
      this.inputForm.controls['maxGroupRegisteredParticipants'].enable();
    }

    if (this.race?.checkpointAmount) {
      this.inputForm.controls.controlPoints.setValue(this.race.checkpointAmount);
    }
    this.inputForm.updateValueAndValidity();
  }


  private initForm() {
    this.inputForm.controls.applicationProcedure.addValidators([required]);
    this.inputForm.controls.typeOfApplication.addValidators([required]);
    this.inputForm.controls.teamType.addValidators([required]);
    this.inputForm.controls.measurementPrecisionType.addValidators([required]);
    this.inputForm.controls.measurementType.addValidators([required]);
    this.inputForm.controls.startByType.addValidators([required]);
    this.inputForm.controls.resultsPerPage.addValidators([required]);
    this.inputForm.controls.topRanked.addValidators([required]);
    this.inputForm.controls.teamRankingGenderSeparation.addValidators([required]);
    this.inputForm.controls.controlPoints.addValidators([required]);
    this.inputForm.controls.penaltyMinutes.addValidators([required]);
    this.inputForm.controls.minimumLapDuration.addValidators([required]);
    this.inputForm.controls.rotatingOrder.addValidators([required]);
    this.inputForm.controls.rotatingResultsSeconds.addValidators([required]);
    this.inputForm.controls.rotatingResultsDisplay.addValidators([required]);
    this.inputForm.controls.category.addValidators([required]);
    this.inputForm.controls.enableGroupRegistrationPayment.addValidators([required]);
    this.inputForm.controls.useGroupRanking.addValidators([required]);
    this.inputForm.controls.timingSystem.addValidators([required]);
    if (this.disableEditing) {
      this.inputForm.disable();
    }
  }

  private initDefaultFields() {
    if (this.raceSettings) {
      this.raceSettings.applicationProcedure = this.raceSettings.applicationProcedure ?? ApplicationProcedureTypes.STANDARD;
      this.raceSettings.teamRankingGenderSeparation = this.raceSettings.teamRankingGenderSeparation ?? TeamRankingTeamSeparationTypes.NOT_USED;
      this.raceSettings.penaltyMinutes = this.raceSettings.penaltyMinutes ?? PenaltyMinutesTypes.NOT_VISIBLE;
      this.raceSettings.useGroupRanking = this.raceSettings.useGroupRanking ?? false;
      if (this.raceSettings.usingUploadRoute === undefined) {
        this.raceSettings.usingUploadRoute = true;
        this.raceSettings.displayControlPoints = true;
        this.raceSettings.showRouteProfile = true;
        this.raceSettings.enableGPXDownload = true;
      }
      this.raceSettings.blockStart = this.raceSettings.blockStart ?? false;
    }
  }

  private subscribeToValueChanges() {
    this.subscriptions.push(this.inputForm.controls.teamType.valueChanges.subscribe((teamType: number | undefined | null) => {
      this.teamTypeChanged(teamType)
    }))

    this.subscriptions.push(this.inputForm.controls.enableGroupRegistrationPayment.valueChanges.subscribe((isUsed: boolean | undefined | null) => {
      this.enableGroupRegistrationPaymentChanged(isUsed);
    }))

    this.subscriptions.push(this.inputForm.controls.timingSystem.valueChanges.subscribe((timingSystem: number | undefined | null) => {
      this.timingSystemChanged(timingSystem)
    }))

    this.subscriptions.push(this.inputForm.controls.useGroupRanking.valueChanges.subscribe((isUsed: boolean | undefined | null) => {
      this.useGroupingChanged(isUsed);
    }))

    this.subscriptions.push(this.inputForm.controls.usingUploadRoute.valueChanges.subscribe((isUsed: boolean) => {
      this.usingUploadRouteChanged(isUsed);
    }))

    this.subscriptions.push(this.inputForm.controls.usingDistanceMarkersOnRoute.valueChanges.subscribe((isUsed: boolean) => {
      this.usingDistanceMarkersOnRouteChanged(isUsed);
    }))

    this.subscriptions.push(this.inputForm.controls.blockStart.valueChanges.subscribe((isUsed: boolean) => {
      this.startBlockChanged(isUsed);
    }))
  }

  private usingUploadRouteChanged(isUsing: boolean) {
    if (isUsing) {
      this.enableField(this.inputForm.controls.showRouteProfile, false, true);
      this.enableField(this.inputForm.controls.enableGPXDownload, false, true);
      this.enableField(this.inputForm.controls.usingDistanceMarkersOnRoute);
    } else {
      this.disableField(this.inputForm.controls.showRouteProfile);
      this.disableField(this.inputForm.controls.enableGPXDownload);
      this.disableField(this.inputForm.controls.usingDistanceMarkersOnRoute);
    }
  }

  private usingDistanceMarkersOnRouteChanged(isUsed: boolean) {
    if (isUsed) {
      this.enableField(this.inputForm.controls.distanceMarkersOnRoute);
    } else {
      this.disableField(this.inputForm.controls.distanceMarkersOnRoute);
    }
  }

  private startBlockChanged(isUsed: boolean) {
    if (isUsed) {
      this.enableField(this.inputForm.controls.maxParticipantsInStartBlock);
    } else {
      this.disableField(this.inputForm.controls.maxParticipantsInStartBlock);
    }
  }


  private teamTypeChanged(teamType: number | undefined | null) {
    switch (teamType) {
      case TeamType.TEAM_1:
      case null:
      case undefined:
        this.disableField(this.inputForm.controls.selectionOfTeamMembersType);
        this.disableField(this.inputForm.controls.teamNameRequired);
        this.disableField(this.inputForm.controls.genderRequired);
        this.enableField(this.inputForm.controls.enableGroupRegistrationPayment);
        this.enableField(this.inputForm.controls.maxGroupRegisteredParticipants);
        break;
      default:
        this.enableField(this.inputForm.controls.selectionOfTeamMembersType, true, RegistrationDisplayFlags.NOT_USED);
        this.enableField(this.inputForm.controls.teamNameRequired, true, false);
        this.enableField(this.inputForm.controls.genderRequired, true, false);
        this.disableField(this.inputForm.controls.enableGroupRegistrationPayment);
        this.disableField(this.inputForm.controls.maxGroupRegisteredParticipants);
        break;
    }
  }

  private enableGroupRegistrationPaymentChanged(isUsed: boolean | undefined | null) {
    if (isUsed) {
      this.enableField(this.inputForm.controls.maxGroupRegisteredParticipants, true);
    } else {
      this.disableField(this.inputForm.controls.maxGroupRegisteredParticipants);
    }
  }

  private timingSystemChanged(timingSystem: number | undefined | null) {
    switch (timingSystem) {
      case TimingSystem.PLATFORM:
      case undefined:
      case null:
        this.disableField(this.inputForm.controls.timingEquipment);
        break;
      default:
        this.enableField(this.inputForm.controls.timingEquipment, true);
        break;
    }
  }

  private useGroupingChanged(isUsed: boolean | undefined | null) {
    if (isUsed) {
      this.enableField(this.inputForm.controls.rankingGroups);
      this.enableField(this.inputForm.controls.participantsFromRankingGroup);
      this.enableField(this.inputForm.controls.groupRankingSeparation, true, GenderSeparationTypes.NOT_USED);
      this.enableField(this.inputForm.controls.teamRankingTeamSeparation, true, TeamRankingTeamSeparationTypes.NOT_USED);
    } else {
      this.disableField(this.inputForm.controls.rankingGroups);
      this.disableField(this.inputForm.controls.participantsFromRankingGroup);
      this.disableField(this.inputForm.controls.groupRankingSeparation);
      this.disableField(this.inputForm.controls.teamRankingTeamSeparation);
    }
  }

  private enableField(formControl: FormControl, addRequired?: boolean, value?: any) {
    formControl.markAsUntouched();
    formControl.enable();
    if (addRequired) {
      formControl.addValidators([required]);
    }
    if (value || value === false) {
      formControl.setValue(value)
    }
    formControl.updateValueAndValidity();
  }

  private disableField(formControl: FormControl, value?: any) {
    formControl.markAsUntouched();
    formControl.removeValidators([required]);
    formControl.setValue(value ?? null);
    formControl.disable();
    formControl.updateValueAndValidity();
  }


  private getRaceSettingsFields(): RaceSettings {
    this.checkValidators(this.inputForm);
    let raceSettings: RaceSettings = {...this.raceSettings};
    if (this.inputForm.controls.rankingGroups.value && raceSettings.useGroupRanking) {
      let rankingGroups: string[] = this.inputForm.controls.rankingGroups.value;
      raceSettings.rankingGroups = rankingGroups.join(";")
    } else {
      raceSettings.rankingGroups = undefined;
    }
    if (!raceSettings.raceId) {
      raceSettings.raceId = this.race?.id;
    }
    raceSettings.applicationProcedure = this.inputForm.controls.applicationProcedure.value;
    raceSettings.typeOfApplication = this.inputForm.controls.typeOfApplication.value;
    raceSettings.teamType = this.inputForm.controls.teamType.value;
    raceSettings.selectionOfTeamMembersType = this.inputForm.controls.selectionOfTeamMembersType.value;
    raceSettings.teamNameRequired = this.inputForm.controls.teamNameRequired.value;
    raceSettings.genderRequired = this.inputForm.controls.genderRequired.value;
    raceSettings.measurementType = this.inputForm.controls.measurementType.value;
    raceSettings.measurementPrecisionType = this.inputForm.controls.measurementPrecisionType.value;
    raceSettings.startByType = this.inputForm.controls.startByType.value;
    raceSettings.resultsPerPage = this.inputForm.controls.resultsPerPage.value;
    raceSettings.topRanked = this.inputForm.controls.topRanked.value;
    raceSettings.teamParticipantsForRanking = this.inputForm.controls.teamParticipantsForRanking.value;
    raceSettings.teamRankingGenderSeparation = this.inputForm.controls.teamRankingGenderSeparation.value;
    raceSettings.participantsFromRankingGroup = this.inputForm.controls.participantsFromRankingGroup.value;
    raceSettings.groupRankingSeparation = this.inputForm.controls.groupRankingSeparation.value;
    raceSettings.teamRankingTeamSeparation = this.inputForm.controls.teamRankingTeamSeparation.value;
    raceSettings.penaltyMinutes = this.inputForm.controls.penaltyMinutes.value;
    raceSettings.controlPoints = this.inputForm.controls.controlPoints.value;
    raceSettings.minimumLapDuration = this.inputForm.controls.minimumLapDuration.value;
    raceSettings.rotatingOrder = this.inputForm.controls.rotatingOrder.value;
    raceSettings.rotatingResultsDisplay = this.inputForm.controls.rotatingResultsDisplay.value;
    raceSettings.rotatingResultsSeconds = this.inputForm.controls.rotatingResultsSeconds.value;
    raceSettings.pic2GoEventId = this.inputForm.controls.pic2GoEventId.value;
    raceSettings.pic2GoEventUniqueCode = this.inputForm.controls.pic2GoEventUniqueCode.value;
    raceSettings.registrationExternalUrl = this.inputForm.controls.registrationExternalUrl.value;
    raceSettings.category = this.categoryList.filter((category: Category) => category.id === this.inputForm.controls.category.value).at(0) ?? undefined;
    raceSettings.enableGroupRegistrationPayment = this.inputForm.controls.enableGroupRegistrationPayment.value;
    raceSettings.maxGroupRegisteredParticipants = this.inputForm.controls.maxGroupRegisteredParticipants.value;
    raceSettings.useGroupRanking = this.inputForm.controls.useGroupRanking.value;
    raceSettings.timingSystem = this.inputForm.controls.timingSystem.value;
    raceSettings.timingEquipment = this.inputForm.controls.timingEquipment.value;
    raceSettings.usingUploadRoute = this.inputForm.controls.usingUploadRoute.value;
    raceSettings.displayControlPoints = this.inputForm.controls.displayControlPoints.value;
    raceSettings.showRouteProfile = this.inputForm.controls.showRouteProfile.value;
    raceSettings.enableGPXDownload = this.inputForm.controls.enableGPXDownload.value;
    raceSettings.usingDistanceMarkersOnRoute = this.inputForm.controls.usingDistanceMarkersOnRoute.value;
    raceSettings.distanceMarkersOnRoute = this.inputForm.controls.distanceMarkersOnRoute.value;
    raceSettings.blockStart = this.inputForm.controls.blockStart.value;
    raceSettings.maxParticipantsInStartBlock = this.inputForm.controls.maxParticipantsInStartBlock.value;
    return raceSettings;
  }


  getDistanceMarkers(): Map<number, string> {
    const optionsMap: Map<number, string> = new Map<number, string>();
    Object.values(DistanceMarkers).map((distanceMarker: any) => {
      if (typeof distanceMarker === 'number') {
        optionsMap.set(distanceMarker, this.translocoService.translate("race.settings.distanceMarkers." + distanceMarker));
      }
    });
    return optionsMap;
  }

  onSave(): void {
    if (!this.inputForm.valid) {
      for (const control in this.inputForm.controls) {
        this.inputForm.get(control)?.markAsTouched();
      }
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.form"))
      return;
    } else if (!this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    } else {
      let raceSettings: RaceSettings = this.getRaceSettingsFields();
      this.loaderService.startLoading();
      this.raceSettingsService.updateRaceSettings(raceSettings, () => {
        if (this.race?.id) {
          this.raceSettingsService.fetchRaceSettings(this.race.id, true)
            .pipe(take(1)).subscribe((settings: RaceSettings | null) => {
            this.loaderService.dismissLoading();
            this.toastService.showToast(TOAST_STATE.success,
              this.translocoService.translate("app.common.success.race.settings.edit"))
            this.raceSettingsChange.emit(settings ?? undefined);
          })
        }
      }, () => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("app.common.server.error"))
      });
    }
  }

  onCancel(): void {
    this.raceSettingsChanged()
  }

  rankingGroupsChanged(tags: string[] | undefined) {
    this.inputForm.controls['rankingGroups'].setValue(tags);
  }


  navigateToOrganiserPage() {
    if (this.race && this.race.organiserId) {
      this.router.navigate([ORGANISER_EDIT], {queryParams: {organiserId: this.race.organiserId}}
      );
    }
  }

  removeTagAtIndex(index: number) {
    let tags = [...this.inputForm.controls.rankingGroups.value]
    tags.splice(index, 1)
    this.inputForm.controls.rankingGroups.setValue(tags)
  }
}

